import axios from 'axios';

// Use relative URL in production (inherits protocol from page), absolute URL in development
const API_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? '/api'  // Relative URL - nginx will proxy to backend
    : 'http://localhost:8000/api'  // Development
);

// Export API_URL for direct use (e.g., constructing media URLs)
export { API_URL };

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // CRITICAL: Send cookies with every request
});

// Response interceptor for 401 handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if not already on a public page
      const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;

      if (!publicPaths.includes(currentPath)) {
        // Session expired or invalid - redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }, {
      headers: { 'Content-Type': 'application/json' },
    }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  requestPasswordReset: (email) => api.post('/auth/password-reset/request', { email }),
  confirmPasswordReset: (token, new_password) =>
    api.post('/auth/password-reset/confirm', { token, new_password }),
  changePassword: (current_password, new_password) =>
    api.post('/auth/password-change', { current_password, new_password }),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  revokeAllSessions: () => api.post('/auth/sessions/revoke-all'),
};

// Users
export const usersAPI = {
  getMe: () => api.get('/users/me'),
  updateMe: (data) => api.put('/users/me', data),
  getUser: (id) => api.get(`/users/${id}`),
  getUsers: (params) => api.get('/users', { params }),
  getUserStatistics: (id) => api.get(`/users/${id}/statistics`),
  getBirthdays: () => api.get('/users/birthdays/all'),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadCoverImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/cover-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeAvatar: () => api.delete('/users/me/avatar'),
  removeCoverImage: () => api.delete('/users/me/cover-image'),
  updateSettings: (settings) => api.patch('/users/me/settings', settings),
  getCalendarToken: () => api.get('/users/me/calendar-token'),
  regenerateCalendarToken: () => api.post('/users/me/calendar-token/regenerate'),
  updateUserGender: (userId, gender) => api.put(`/users/${userId}/gender`, { gender }),
  updateUsername: (new_username) => api.put('/users/me/username', { new_username }),
  requestEmailChange: (new_email, password) => api.post('/users/me/email/request', { new_email, password }),
  confirmEmailChange: (token) => api.post('/users/me/email/confirm', { token }),
  changePassword: (current_password, new_password) => api.post('/auth/password-change', { current_password, new_password }),
};

// Locations
export const locationsAPI = {
  getLocations: (params) => api.get('/locations', { params }),
  getLocation: (id) => api.get(`/locations/${id}`),
  createLocation: (data) => api.post('/locations', data),
  updateLocation: (id, data) => api.put(`/locations/${id}`, data),
  deleteLocation: (id) => api.delete(`/locations/${id}`),
  uploadLocationImage: (id, formData) => api.post(`/locations/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getLocationImage: (id) => api.get(`/locations/${id}/image`),
  uploadLocationMenu: (id, formData) => api.post(`/locations/${id}/menu`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getLocationMenu: (id) => api.get(`/locations/${id}/menu`, { responseType: 'blob' }),
};

// Reviews
export const reviewsAPI = {
  getReviews: (params) => api.get('/reviews', { params }),
  getReview: (id) => api.get(`/reviews/${id}`),
  createReview: (data) => api.post('/reviews', data),
  updateReview: (id, data) => api.put(`/reviews/${id}`, data),
  deleteReview: (id) => api.delete(`/reviews/${id}`),
  getComments: (reviewId) => api.get(`/reviews/${reviewId}/comments`),
  createComment: (data) => api.post('/reviews/comments', data),
  getReactions: (reviewId) => api.get(`/reviews/${reviewId}/reactions`),
  createReaction: (data) => api.post('/reviews/reactions', data),
};

// Events
export const eventsAPI = {
  getEvents: (params) => api.get('/events', { params }),
  getEvent: (id) => api.get(`/events/${id}`),
  createEvent: (data) => api.post('/events', data),
  updateEvent: (id, data) => api.put(`/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/events/${id}`),
  uploadEventCover: (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/events/${eventId}/cover`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTodos: (eventId) => api.get(`/events/${eventId}/todos`),
  createTodo: (data) => api.post('/events/todos', data),
  updateTodo: (id, data) => api.put(`/events/todos/${id}`, data),
  deleteTodo: (id) => api.delete(`/events/todos/${id}`),
  createTodayBeachEvent: () => api.post('/events/beach/create-today'),
  completeBeachTask: (taskId) => api.post(`/events/beach/tasks/${taskId}/complete`),
  uncompleteBeachTask: (taskId) => api.delete(`/events/beach/tasks/${taskId}/complete`),
  getBeachTaskCompletions: (taskId) => api.get(`/events/beach/tasks/${taskId}/completions`),
  adminAddBeachCompletion: (taskId, userId) => api.post(`/events/beach/tasks/${taskId}/completions/admin`, { user_id: userId }),
  adminRemoveBeachCompletion: (taskId, userId) => api.delete(`/events/beach/tasks/${taskId}/completions/${userId}/admin`),
  claimTask: (todoId) => api.post(`/events/todos/${todoId}/claim`),
  unclaimTask: (todoId) => api.post(`/events/todos/${todoId}/unclaim`),
  completeTask: (todoId) => api.post(`/events/todos/${todoId}/complete`),
  complainTask: (todoId, reason) => api.post(`/events/todos/${todoId}/complain`, { reason }),
  getAdminComplaints: (resolved = null) => api.get('/events/todos/admin-complaints', { params: resolved !== null ? { resolved } : {} }),
  adminResolveComplaint: (todoId, complaintId, data) => api.post(`/events/todos/${todoId}/complaints/${complaintId}/admin-resolve`, data),
  getTaskLogs: (todoId) => api.get(`/events/todos/${todoId}/logs`),
  getPolls: (eventId) => api.get(`/events/${eventId}/polls`),
  createPoll: (data) => api.post('/events/polls', data),
  votePoll: (data) => api.post('/events/polls/vote', data),
  addPollOption: (data) => api.post('/events/polls/options', data),
  deletePoll: (pollId) => api.delete(`/events/polls/${pollId}`),
  joinEvent: (eventId) => api.post(`/events/${eventId}/join`),
  leaveEvent: (eventId) => api.delete(`/events/${eventId}/leave`),
  getParticipants: (eventId) => api.get(`/events/${eventId}/participants`),
  // Delay / penalty endpoints
  getParticipantsForDelays: (eventId) => api.get(`/events/${eventId}/participants-delays`),
  getPenaltiesStatus: (eventId) => api.get(`/events/${eventId}/penalties-status`),
  submitDelays: (eventId, data) => api.post(`/events/${eventId}/delays`, data),
  // Admin endpoints
  adminAddParticipant: (eventId, userId) => api.post(`/events/${eventId}/participants/admin-add`, { user_id: userId }),
  adminRemoveParticipant: (eventId, userId) => api.delete(`/events/${eventId}/participants/${userId}/admin-remove`),
  adminCompleteTask: (todoId) => api.patch(`/events/todos/${todoId}/admin-complete`),
  adminUpdateTaskStatus: (todoId, status) => api.patch(`/events/todos/${todoId}/admin-update-status`, { status }),
  adminUpdateTaskType: (todoId, taskType) => api.patch(`/events/todos/${todoId}/admin-update-type`, { task_type: taskType }),
  adminUpdateTaskOwners: (todoId, ownerIds) => api.patch(`/events/todos/${todoId}/admin-update-owners`, { owner_ids: ownerIds }),
  adminDeleteTask: (todoId) => api.delete(`/events/todos/${todoId}/admin-delete`),
  adminRecalculateEvent: (eventId, confirm = true, dryRun = false) => api.post(`/events/${eventId}/admin-recalculate`, { confirm, dry_run: dryRun }),
};

// Gamification
export const gamificationAPI = {
  getBadges: (params) => api.get('/gamification/badges', { params }),
  getBadgesWithProgress: () => api.get('/gamification/badges/with-progress'),
  getBadge: (id) => api.get(`/gamification/badges/${id}`),
  createBadge: (formData) => api.post('/gamification/badges', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateBadge: (id, formData) => api.put(`/gamification/badges/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteBadge: (id) => api.delete(`/gamification/badges/${id}`),
  seedBadge: (badgeId) => api.post(`/gamification/badges/${badgeId}/seed`),
  getBadgeSvg: (badgeId) => api.get(`/gamification/badges/${badgeId}/svg`, { responseType: 'blob' }),
  getUserBadges: (userId) => api.get(`/gamification/users/${userId}/badges`),
  assignBadge: (userId, badgeId) => api.post(`/gamification/users/${userId}/badges/${badgeId}`),
  removeBadge: (userId, badgeId) => api.delete(`/gamification/users/${userId}/badges/${badgeId}`),
  awardPoints: (userId, data) => api.post(`/gamification/users/${userId}/points`, data),
  getPointTransactions: (userId, params) =>
    api.get(`/gamification/users/${userId}/points`, { params }),
  getAllTransactions: (params) => api.get('/gamification/transactions', { params }),
  deleteTransaction: (transactionId) => api.delete(`/gamification/transactions/${transactionId}`),
  updateTransaction: (transactionId, data) =>
    api.patch(`/gamification/transactions/${transactionId}`, data),
  reportTransaction: (transactionId, reason) =>
    api.post(`/gamification/transactions/${transactionId}/report`, { report_reason: reason }),
  updateMultiplier: (userId, multiplier) =>
    api.put(`/gamification/users/${userId}/multiplier`, { multiplier }),
  giveKudos: (userId, points, reason) =>
    api.post(`/gamification/users/${userId}/kudos`, { points, reason }),
  getKudosRemaining: (userId) => api.get(`/gamification/users/${userId}/kudos-remaining`),
  resetAllKudos: () => api.post('/gamification/kudos/reset-all'),
  manageUserKudos: (userId, kudosAmount, operation = 'set') =>
    api.put(`/gamification/users/${userId}/kudos`, { kudos_amount: kudosAmount, operation }),
  getLeaderboard: (params) => api.get('/gamification/leaderboard', { params }),
};

// Challenges
export const challengesAPI = {
  getChallenges: (params) => api.get('/challenges', { params }),
  getChallenge: (id) => api.get(`/challenges/${id}`),
  createChallenge: (data) => api.post('/challenges', data),
  updateChallenge: (id, data) => api.put(`/challenges/${id}`, data),
  deleteChallenge: (id) => api.delete(`/challenges/${id}`),
  joinChallenge: (id, optionId) => api.post(`/challenges/${id}/join`, { option_id: optionId }),
  changeOption: (id, optionId) => api.put(`/challenges/${id}/change-option`, { option_id: optionId }),
  resolveChallenge: (id, winningOptionId) =>
    api.post(`/challenges/${id}/resolve`, { winning_option_id: winningOptionId }),
  getParticipations: (id) => api.get(`/challenges/${id}/participations`),
  getOptionParticipants: (challengeId, optionId) =>
    api.get(`/challenges/${challengeId}/options/${optionId}/participants`),
  getUserStats: () => api.get('/challenges/user/stats'),
};

// Notifications
export const notificationsAPI = {
  // Push subscriptions
  subscribe: (data) => api.post('/notifications/subscribe', data),
  unsubscribe: (id) => api.delete(`/notifications/unsubscribe/${id}`),
  sendNotification: (data, userIds) =>
    api.post('/notifications/send', data, { params: { user_ids: userIds } }),

  // In-app notifications
  getNotifications: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`, { is_read: true }),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),

  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),

  // Admin notifications
  sendAdminNotification: (data) => api.post('/notifications/admin/send-notification', data),
};

// Media
export const mediaAPI = {
  uploadPhoto: (eventId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/media/events/${eventId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  uploadVideo: (eventId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/media/events/${eventId}/videos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  getAllMedia: (params) => api.get('/media/all', { params }),
  getEventMedia: (eventId, mediaType) =>
    api.get(`/media/events/${eventId}/media`, { params: { media_type: mediaType } }),
  getMediaFile: (mediaId) => api.get(`/media/${mediaId}`, { responseType: 'blob' }),
  getMediaJpeg: (mediaId) => api.get(`/media/${mediaId}/jpeg`, { responseType: 'blob' }),
  getMediaThumbnail: (mediaId) => api.get(`/media/${mediaId}/thumbnail`, { responseType: 'blob' }),
  getMediaOriginal: (mediaId) => api.get(`/media/${mediaId}/original`, { responseType: 'blob' }),
  getVideoQuality: (mediaId, quality, format = 'mp4') =>
    api.get(`/media/${mediaId}/video/${quality}?format=${format}`, { responseType: 'blob' }),
  getHlsPlaylist: (mediaId) => api.get(`/media/${mediaId}/hls`),
  getDashManifest: (mediaId) => api.get(`/media/${mediaId}/dash`),
  deleteMedia: (mediaId) => api.delete(`/media/${mediaId}`),
  getMediaStats: (eventId) => api.get(`/media/events/${eventId}/media/stats`),
  uploadBugScreenshot: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/media/upload/bug-screenshot', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  toggleHeart: (mediaId) => api.post(`/media/${mediaId}/heart`),
  getMediaHearts: (mediaId) => api.get(`/media/${mediaId}/hearts`),
};

// Support
export const supportAPI = {
  submitSuggestion: (data) => api.post('/support/suggestion', data),
  submitBugReport: (data) => api.post('/support/bug-report', data),
};

// Statistics
export const statisticsAPI = {
  getCollectiveStats: (forceRefresh = false) =>
    api.get('/statistics/collective', { params: { force_refresh: forceRefresh } }),
};

// Moods
export const moodsAPI = {
  getConfig: () => api.get('/moods/config'),
  getCurrentMood: () => api.get('/moods/me/current'),
  createMoodEntry: (data) => api.post('/moods/me', data),
  updateMoodEntry: (moodId, data) => api.patch(`/moods/me/${moodId}`, data),
  resetCurrentMood: () => api.delete('/moods/me/current'),
  getMyHistory: (limit = 30) => api.get('/moods/me/history', { params: { limit } }),
  getUserMood: (userId) => api.get(`/moods/users/${userId}/mood`),
};

// Year End
export const yearEndAPI = {
  closeYear: (year, confirmation) => api.post('/year-end/close-year', { year, confirmation }),
  getHallOfFame: () => api.get('/year-end/hall-of-fame'),
};

// Wishlists
export const wishlistsAPI = {
  getWishlists: (status = 'all') => api.get('/wishlists', { params: { status } }),
  createWishlist: (data) => api.post('/wishlists', data),
  updateWishlist: (id, data) => api.put(`/wishlists/${id}`, data),
  deleteWishlist: (id) => api.delete(`/wishlists/${id}`),
  toggleStar: (id) => api.post(`/wishlists/${id}/star`),
  getWishlistStars: (id) => api.get(`/wishlists/${id}/stars`),
  toggleRealized: (id) => api.post(`/wishlists/${id}/realize`)
};

export default api;
